"use client";

// Mobile hamburger menu with slide-out drawer.
// Visible only below the md breakpoint; pairs with
// `hidden md:flex` on the desktop nav link group.

import { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { Menu, X, ArrowRight, Search } from "lucide-react";
import { PRIMARY_NAV_LINKS } from "@/lib/nav-links";
import { FOCUSABLE_SELECTOR, trapFocusTarget } from "@/lib/dialog-focus";

export default function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  // The drawer's link list is not rendered until the first open. It is hidden
  // (inert, off-screen) while closed, but the same links already sit in the
  // desktop header row, so server-rendering both put every nav label in the
  // HTML twice. After the first open it stays mounted so closing still slides
  // the full drawer out.
  const [everOpened, setEverOpened] = useState(false);

  const openerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Dialog behavior while open: move focus into the drawer, close on Escape,
  // trap Tab, and hand focus back to the hamburger on close.
  useEffect(() => {
    if (!isOpen) return;
    const opener = openerRef.current;
    closeRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const items = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      const target = trapFocusTarget(
        items.length,
        items.indexOf(document.activeElement as HTMLElement),
        e.shiftKey,
      );
      if (target !== null) {
        e.preventDefault();
        items[target].focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      opener?.focus();
    };
  }, [isOpen]);

  return (
    <div className="md:hidden">
      {/* Hamburger toggle */}
      <button
        ref={openerRef}
        type="button"
        onClick={() => {
          setEverOpened(true);
          setIsOpen((o) => !o);
        }}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls="mobile-menu-drawer"
        className="-mr-2 inline-flex min-h-12 min-w-12 items-center justify-center text-content-muted hover:text-content-text transition-colors"
      >
        {isOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-content-bg/60 backdrop-blur-sm"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Slide-out drawer */}
      <div
        ref={drawerRef}
        id="mobile-menu-drawer"
        role="dialog"
        aria-modal={isOpen ? true : undefined}
        aria-label="Site menu"
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={`fixed top-0 right-0 z-50 h-full w-72 bg-content-surface border-l border-content-border/50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-content-border/50">
          <span className="text-forge-trust font-bold text-lg">
            Menu
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label="Close menu"
            className="inline-flex min-h-12 min-w-12 items-center justify-center text-content-muted hover:text-content-text transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav links (mounted on first open, see everOpened) */}
        {everOpened && (
          <nav aria-label="Mobile navigation" className="px-6 py-6 flex flex-col gap-1">
            {/* Site search — plain GET form to /reviews?q=, reachable on
                narrow viewports where the header search box is hidden. */}
            <form action="/reviews" method="GET" role="search" className="mb-4">
              <label htmlFor="mobile-site-search" className="sr-only">
                Search reviews
              </label>
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted"
                />
                <input
                  id="mobile-site-search"
                  type="search"
                  name="q"
                  maxLength={100}
                  placeholder="Search products…"
                  className="min-h-12 w-full rounded-lg border border-content-border/50 bg-content-elevated py-2.5 pl-9 pr-3 text-base text-content-text placeholder:text-content-muted focus:outline-none focus:ring-1 focus:ring-forge-trust/50"
                />
              </div>
            </form>

            <ul className="flex flex-col gap-1">
              {PRIMARY_NAV_LINKS.map(({ href, label }) => (
                <li key={href + label}>
                  <Link
                    href={href}
                    onClick={close}
                    className="flex min-h-12 items-center rounded-lg px-3 py-3 text-base font-medium text-content-muted hover:bg-content-elevated hover:text-content-text transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Trust-blue CTA */}
            <Link
              href="/#get-guide"
              onClick={close}
              className="mt-4 flex min-h-12 items-center justify-center gap-1.5 rounded-lg bg-forge-trust/10 border border-forge-trust/20 px-4 py-3 text-base font-semibold text-forge-trust transition-all hover:bg-forge-trust hover:text-content-bg"
            >
              Get updates
              <ArrowRight size={14} />
            </Link>
          </nav>
        )}
      </div>
    </div>
  );
}
